import { createServerFn } from "@tanstack/react-start";
import { notFound } from "@tanstack/react-router";
import { getContent } from "./get-content";
import { getLocationsByIds } from "./get-locations";
import z from "zod";
import { db } from "~/db";
import { pages } from "~/db/schema/typo3/pages.schema";
import { and, eq } from "drizzle-orm";

export const getCmsPageContent = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: z.string() }))
  .handler(async ({ data }) => {
    async function normalize(item: any): Promise<any> {
      const base: any = {
        uid: item.uid,
        type: item.ctype,
        data: item.data,
      };

      if (Array.isArray(item.children) && item.children.length > 0) {
        const normalizedChildren = [];
        for (const child of item.children) {
          normalizedChildren.push(await normalize(child));
        }
        return { ...base, children: normalizedChildren };
      }

      return base;
    }

    try {
      const [page] = await db
        .select({
          title: pages.title,
        })
        .from(pages)
        .where(
          and(
            eq(pages.slug, data.slug),
            eq(pages.deleted, 0),
            eq(pages.hidden, 0),
            eq(pages.doktype, 1),
          ),
        )
        .limit(1)
        .execute();

      const rawContent = await getContent({ data: { slug: data.slug } });
      if (!rawContent) return { content: [], pageTitle: page?.title ?? null, slug: data.slug };

      const content = [];
      for (const item of rawContent) {
        content.push(await normalize(item));
      }

      return { content, pageTitle: page?.title ?? null, slug: data.slug };
    } catch (error: any) {
      // Re-throw notFound() errors to ensure they propagate correctly
      // This is important because server function errors get serialized
      if (error && typeof error === "object") {
        if (error.name === "NotFoundError" || error.constructor?.name === "NotFoundError") {
          throw notFound();
        }
        // Check for 404 status
        if (error.status === 404 || error.statusCode === 404) {
          throw notFound();
        }
        // Check error message - TanStack Start serializes notFound() as JSON in the message
        if (error.message && typeof error.message === "string") {
          // Try to parse as JSON (TanStack Start serializes notFound() as {"isNotFound":true})
          try {
            const parsed = JSON.parse(error.message);
            if (parsed && typeof parsed === "object" && parsed.isNotFound === true) {
              throw notFound();
            }
          } catch {
            // Not JSON, check for text patterns
            const msg = error.message.toLowerCase();
            if (msg.includes("not found") || msg.includes("404")) {
              throw notFound();
            }
          }
        }
      }
      // Re-throw other errors
      throw error;
    }
  });
